/*
 * Every route, in one place — imported by the router AND by vite.config.ts.
 *
 * The build emits a real HTML file per route (`billing.html`, `share.html`, …) rather than relying on a
 * Front Door SPA rewrite, because scripts/base/upload-site.mjs already uploads an extensionless
 * `text/html` copy of every page. That gives `/billing` a genuine 200 from storage with no rule to
 * maintain, and leaves a real miss answering 404 as it should. Adding a route here is the whole change.
 *
 * This file must stay dependency-free: vite.config.ts imports it before any Svelte plugin has run.
 */
export type RouteName =
  | 'summary'
  | 'billing'
  | 'marketing'
  | 'share'
  | 'delete'
  | 'sign-in'
  | 'forgot-password'
  | 'password-reset'
  | 'email-preferences'

export type Route = {
  name: RouteName
  /** The URL path. `/` is the account home, which is Summary. */
  path: string
  /** The file the build emits. The home page is index.html; the rest take their own name. */
  file: string
  /** Browser tab title, set by the router. */
  title: string
  /** Signed-out routes. Everything else redirects to sign-in, preserving where they were heading. */
  anonymous?: true
  /** Shown in the account navigation, in this order. */
  nav?: string
  /**
   * Reachable in live. Omitted until the route's API exists — the deploy uploads only released shells, so
   * an unreleased route is a genuine 404 from storage rather than a page that half works. Design
   * `platform-architecture` §6a.3a; scripts/base/upload-site.mjs is what acts on it.
   */
  released?: true
}

export const ROUTES: Route[] = [
  { name: 'summary', path: '/', file: 'index.html', title: 'Account summary', nav: 'Account summary' },
  { name: 'billing', path: '/billing', file: 'billing.html', title: 'Billing info', nav: 'Billing info' },
  {
    name: 'marketing',
    path: '/marketing',
    file: 'marketing.html',
    title: 'Marketing preferences',
    nav: 'Marketing preferences',
  },
  { name: 'share', path: '/share', file: 'share.html', title: 'Share Bird Journal', nav: 'Share Bird Journal' },
  {
    name: 'delete',
    path: '/delete',
    file: 'delete.html',
    title: 'Delete your account',
    nav: 'Request account deletion',
  },
  { name: 'sign-in', path: '/sign-in', file: 'sign-in.html', title: 'Sign in', anonymous: true },
  {
    name: 'forgot-password',
    path: '/forgot-password',
    file: 'forgot-password.html',
    title: 'Forgotten password',
    anonymous: true,
  },
  {
    name: 'password-reset',
    path: '/password-reset',
    file: 'password-reset.html',
    title: 'Choose a new password',
    anonymous: true,
  },
  {
    name: 'email-preferences',
    path: '/email-preferences',
    file: 'email-preferences.html',
    title: 'Email preferences',
    // The token in `?t=` is the credential, so this page must never bounce to sign-in.
    anonymous: true,
    released: true,
  },
]

export const NAV_ROUTES = ROUTES.filter((route) => route.nav)

export const RELEASED_ROUTES = ROUTES.filter((route) => route.released)

export const routeByPath = (pathname: string): Route | undefined => {
  // Tolerate the trailing slash a storage static website hands back for a directory-style request, and
  // the extension a deep link may carry if somebody bookmarked the underlying file.
  const trimmed = pathname.replace(/\.html$/, '').replace(/\/+$/, '') || '/'
  return ROUTES.find((route) => route.path === trimmed)
}
